import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Trash2, KeyRound, Search, Shield, X, ChevronDown, ChevronUp, UserCog, Check, Mail } from "lucide-react";
import { SITE_OWNER_EMAIL } from "@shared/constants";
import type { NewsletterLead } from "@shared/schema";

const AVAILABLE_ROLES = ["Blog Writer", "Forum Moderator", "User Support", "Newsletter Writer"];

interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  email: string | null;
  isAdmin: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  roles: string[];
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) +
    " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

type SortField = "createdAt" | "lastLoginAt" | "username" | "email";
type SortDir = "asc" | "desc";

export default function UserAnalytics() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [resetPasswordId, setResetPasswordId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [rolesUserId, setRolesUserId] = useState<string | null>(null);
  const [pendingRoles, setPendingRoles] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const isSiteOwner = (user?.email || "").toLowerCase() === SITE_OWNER_EMAIL;

  const { data: users = [], isLoading } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users"],
    queryFn: async () => {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load users");
      return res.json();
    },
    enabled: !!user?.isAdmin,
  });

  const [leadSearch, setLeadSearch] = useState("");

  const { data: newsletterLeads = [], isLoading: leadsLoading } = useQuery<NewsletterLead[]>({
    queryKey: ["/api/admin/newsletter-leads"],
    queryFn: async () => {
      const res = await fetch("/api/admin/newsletter-leads", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load newsletter subscribers");
      return res.json();
    },
    enabled: !!user?.isAdmin && isSiteOwner,
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Delete failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User deleted" });
      setDeleteConfirmId(null);
      setDeleteConfirmText("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      const res = await fetch(`/api/admin/users/${userId}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ newPassword }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Reset failed");
      }
    },
    onSuccess: () => {
      toast({ title: "Password reset successfully" });
      setResetPasswordId(null);
      setNewPassword("");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const rolesMutation = useMutation({
    mutationFn: async ({ userId, roles }: { userId: string; roles: string[] }) => {
      const res = await fetch(`/api/admin/users/${userId}/roles`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ roles }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to update roles");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Roles updated" });
      setRolesUserId(null);
      setPendingRoles([]);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (!user?.isAdmin) {
    return (
      <>
        <Navbar />
        <main id="main-content" className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Shield size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-display font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground">This page is only accessible to site administrators.</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      u.username.toLowerCase().includes(q) ||
      (u.firstName || "").toLowerCase().includes(q) ||
      (u.lastName || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.companyName || "").toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    let aVal: string | null = null;
    let bVal: string | null = null;
    if (sortField === "createdAt") {
      aVal = a.createdAt;
      bVal = b.createdAt;
    } else if (sortField === "lastLoginAt") {
      aVal = a.lastLoginAt;
      bVal = b.lastLoginAt;
    } else if (sortField === "username") {
      aVal = a.username.toLowerCase();
      bVal = b.username.toLowerCase();
    } else if (sortField === "email") {
      aVal = (a.email || "").toLowerCase();
      bVal = (b.email || "").toLowerCase();
    }
    if (!aVal && !bVal) return 0;
    if (!aVal) return 1;
    if (!bVal) return -1;
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDir === "asc" ? cmp : -cmp;
  });

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  }

  function openRolesModal(u: AdminUser) {
    setRolesUserId(u.id);
    setPendingRoles([...(u.roles || [])]);
  }

  function toggleRole(role: string) {
    setPendingRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Admin</span>
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold mb-4" data-testid="text-user-management-title">
                User Management
              </h1>
              <p className="text-lg text-muted-foreground">
                {users.length} registered {users.length === 1 ? "user" : "users"}
              </p>
            </div>

            <div className="mb-8 flex flex-col sm:flex-row gap-4">
              <div className="relative max-w-md flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  placeholder="Search by name, username, email, or company..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  aria-label="Search users"
                  data-testid="input-search-users"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={sortField === "username" && sortDir === "asc" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setSortField("username"); setSortDir("asc"); }}
                  data-testid="button-sort-az"
                  className="text-xs"
                >
                  A → Z
                </Button>
                <Button
                  variant={sortField === "username" && sortDir === "desc" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setSortField("username"); setSortDir("desc"); }}
                  data-testid="button-sort-za"
                  className="text-xs"
                >
                  Z → A
                </Button>
                <Button
                  variant={sortField === "lastLoginAt" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setSortField("lastLoginAt"); setSortDir("desc"); }}
                  data-testid="button-sort-last-login"
                  className="text-xs"
                >
                  Last Login
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-20 text-muted-foreground">Loading users...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="table-users">
                  <thead>
                    <tr className="border-b border-white/10 text-left">
                      <th className="pb-3 pr-4 font-semibold">
                        <button
                          onClick={() => toggleSort("username")}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                          data-testid="button-sort-username"
                        >
                          Username <SortIcon field="username" />
                        </button>
                      </th>
                      <th className="pb-3 pr-4 font-semibold">First Name</th>
                      <th className="pb-3 pr-4 font-semibold">Last Name</th>
                      <th className="pb-3 pr-4 font-semibold">Company</th>
                      <th className="pb-3 pr-4 font-semibold">
                        <button
                          onClick={() => toggleSort("email")}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                          data-testid="button-sort-email"
                        >
                          Email <SortIcon field="email" />
                        </button>
                      </th>
                      <th className="pb-3 pr-4 font-semibold">Roles</th>
                      <th className="pb-3 pr-4 font-semibold">
                        <button
                          onClick={() => toggleSort("createdAt")}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                          data-testid="button-sort-registered"
                        >
                          Registered <SortIcon field="createdAt" />
                        </button>
                      </th>
                      <th className="pb-3 pr-4 font-semibold">IP Address</th>
                      <th className="pb-3 pr-4 font-semibold">
                        <button
                          onClick={() => toggleSort("lastLoginAt")}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                          data-testid="button-sort-last-login"
                        >
                          Last Login <SortIcon field="lastLoginAt" />
                        </button>
                      </th>
                      <th className="pb-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((u) => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`row-user-${u.id}`}>
                        <td className="py-4 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium" data-testid={`text-username-${u.id}`}>{u.username}</span>
                            {u.isAdmin && (
                              <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
                                Admin
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground" data-testid={`text-firstname-${u.id}`}>
                          {u.firstName || "—"}
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground" data-testid={`text-lastname-${u.id}`}>
                          {u.lastName || "—"}
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground" data-testid={`text-company-${u.id}`}>
                          {u.companyName || "—"}
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground" data-testid={`text-email-${u.id}`}>
                          {u.email || "—"}
                        </td>
                        <td className="py-4 pr-4" data-testid={`text-roles-${u.id}`}>
                          {u.roles && u.roles.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {u.roles.map((role) => (
                                <Badge key={role} variant="secondary" className="text-[10px] whitespace-nowrap">
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground whitespace-nowrap" data-testid={`text-registered-${u.id}`}>
                          {formatDate(u.createdAt)}
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground font-mono text-xs" data-testid={`text-ip-${u.id}`}>
                          {u.lastLoginIp || "—"}
                        </td>
                        <td className="py-4 pr-4 text-muted-foreground whitespace-nowrap" data-testid={`text-lastlogin-${u.id}`}>
                          {formatDate(u.lastLoginAt)}
                        </td>
                        <td className="py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRolesModal(u)}
                              aria-label={`Manage roles for ${u.username}`}
                              data-testid={`button-roles-${u.id}`}
                            >
                              <UserCog size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setResetPasswordId(u.id);
                                setNewPassword("");
                              }}
                              aria-label={`Reset password for ${u.username}`}
                              data-testid={`button-reset-password-${u.id}`}
                            >
                              <KeyRound size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => setDeleteConfirmId(u.id)}
                              disabled={u.id === user?.id}
                              aria-label={`Delete user ${u.username}`}
                              data-testid={`button-delete-user-${u.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {sorted.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    {search ? "No users match your search." : "No registered users."}
                  </div>
                )}
              </div>
            )}

            {isSiteOwner && (
              <div id="newsletter-subs" className="mt-24 pt-16 border-t border-white/10">
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Mail size={22} className="text-primary" aria-hidden="true" />
                    <h2 className="text-2xl sm:text-3xl font-display font-bold" data-testid="text-newsletter-subs-title">
                      Newsletter Subs
                    </h2>
                  </div>
                  <p className="text-muted-foreground">
                    {newsletterLeads.length} lead-magnet {newsletterLeads.length === 1 ? "signup" : "signups"} (playbook and other forms)
                  </p>
                </div>

                <div className="mb-6 max-w-md">
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                    <Input
                      placeholder="Search by name or email..."
                      value={leadSearch}
                      onChange={(e) => setLeadSearch(e.target.value)}
                      className="pl-10"
                      aria-label="Search newsletter subscribers"
                      data-testid="input-search-newsletter-leads"
                    />
                  </div>
                </div>

                {leadsLoading ? (
                  <div className="text-center py-12 text-muted-foreground">Loading subscribers...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="table-newsletter-leads">
                      <thead>
                        <tr className="border-b border-white/10 text-left">
                          <th className="pb-3 pr-4 font-semibold">Full name</th>
                          <th className="pb-3 pr-4 font-semibold">Email</th>
                          <th className="pb-3 pr-4 font-semibold">Source</th>
                          <th className="pb-3 font-semibold">Signed up</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsletterLeads
                          .filter((lead) => {
                            const q = leadSearch.toLowerCase();
                            if (!q) return true;
                            return (
                              lead.name.toLowerCase().includes(q) ||
                              lead.email.toLowerCase().includes(q) ||
                              (lead.source || "").toLowerCase().includes(q)
                            );
                          })
                          .map((lead) => (
                            <tr
                              key={lead.id}
                              className="border-b border-white/5 hover:bg-white/[0.02]"
                              data-testid={`row-newsletter-lead-${lead.id}`}
                            >
                              <td className="py-4 pr-4 font-medium" data-testid={`text-lead-name-${lead.id}`}>
                                {lead.name}
                              </td>
                              <td className="py-4 pr-4 text-muted-foreground" data-testid={`text-lead-email-${lead.id}`}>
                                {lead.email}
                              </td>
                              <td className="py-4 pr-4 text-muted-foreground text-xs">
                                {lead.source || "—"}
                              </td>
                              <td className="py-4 text-muted-foreground whitespace-nowrap">
                                {formatDate(
                                  lead.createdAt instanceof Date
                                    ? lead.createdAt.toISOString()
                                    : String(lead.createdAt)
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {newsletterLeads.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">No newsletter signups yet.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      {rolesUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" role="dialog" aria-modal="true" aria-label="Manage user roles">
          <Card className="max-w-md w-full mx-4 p-6 bg-card border border-white/10">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg font-display font-semibold">Manage Roles</h2>
              <button onClick={() => { setRolesUserId(null); setPendingRoles([]); }} className="p-2 -m-1 rounded-md hover:bg-white/5" aria-label="Close roles dialog">
                <X size={18} className="text-muted-foreground" />
              </button>
            </div>
            <p className="text-muted-foreground mb-6">
              Assign roles to <strong className="text-foreground">{users.find((u) => u.id === rolesUserId)?.username}</strong>
            </p>
            <div className="space-y-2 mb-6">
              {AVAILABLE_ROLES.map((role) => {
                const isActive = pendingRoles.includes(role);
                return (
                  <button
                    key={role}
                    onClick={() => toggleRole(role)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors text-left ${
                      isActive
                        ? "border-primary/50 bg-primary/10 text-foreground"
                        : "border-white/10 hover:border-white/20 text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`button-toggle-role-${role.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="text-sm font-medium">{role}</span>
                    {isActive && <Check size={16} className="text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setRolesUserId(null); setPendingRoles([]); }} data-testid="button-cancel-roles">
                Cancel
              </Button>
              <Button
                onClick={() => rolesMutation.mutate({ userId: rolesUserId, roles: pendingRoles })}
                disabled={rolesMutation.isPending}
                data-testid="button-save-roles"
              >
                {rolesMutation.isPending ? "Saving..." : "Save Roles"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" role="dialog" aria-modal="true" aria-label="Confirm user deletion">
          <Card className="max-w-md w-full mx-4 p-6 bg-card border border-white/10">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg font-display font-semibold text-red-400">Delete User</h2>
              <button onClick={() => { setDeleteConfirmId(null); setDeleteConfirmText(""); }} className="p-2 -m-1 rounded-md hover:bg-white/5" aria-label="Cancel deletion">
                <X size={18} className="text-muted-foreground" />
              </button>
            </div>
            <p className="text-muted-foreground mb-2">
              Are you sure you want to delete <strong className="text-foreground">{users.find((u) => u.id === deleteConfirmId)?.username}</strong>?
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              This will permanently remove their account, forum posts, and all associated data.
            </p>
            <div className="mb-6">
              <label htmlFor="delete-confirm" className="block text-sm font-medium mb-2">
                Type <strong className="text-foreground">"delete"</strong> to confirm
              </label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder='Type "delete" here'
                data-testid="input-delete-confirm"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setDeleteConfirmId(null); setDeleteConfirmText(""); }} data-testid="button-cancel-delete">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                disabled={deleteMutation.isPending || deleteConfirmText.toLowerCase() !== "delete"}
                data-testid="button-confirm-delete"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete User"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {resetPasswordId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" role="dialog" aria-modal="true" aria-label="Reset user password">
          <Card className="max-w-md w-full mx-4 p-6 bg-card border border-white/10">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg font-display font-semibold">Reset Password</h2>
              <button onClick={() => { setResetPasswordId(null); setNewPassword(""); }} className="p-2 -m-1 rounded-md hover:bg-white/5" aria-label="Cancel password reset">
                <X size={18} className="text-muted-foreground" />
              </button>
            </div>
            <p className="text-muted-foreground mb-4">
              Set a new password for <strong className="text-foreground">{users.find((u) => u.id === resetPasswordId)?.username}</strong>.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (resetPasswordId) {
                  resetMutation.mutate({ userId: resetPasswordId, newPassword });
                }
              }}
            >
              <div className="mb-6">
                <label htmlFor="new-password" className="block text-sm font-medium mb-2">New Password</label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  minLength={6}
                  required
                  data-testid="input-new-password"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" type="button" onClick={() => { setResetPasswordId(null); setNewPassword(""); }} data-testid="button-cancel-reset">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetMutation.isPending || newPassword.length < 6}
                  data-testid="button-confirm-reset"
                >
                  {resetMutation.isPending ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <Footer />
    </>
  );
}
